/**
 * MobileEvaluationsTopMenu
 *
 * Mobile-optimized top navigation for EvaluationsView.
 * Uses MUI ToggleButtonGroup for view switching (Collab / DM)
 * and a simple year picker with chevron arrows.
 */
import React from "react";
import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Langs from "../../../app/lang/langs";
import { TeamSelector } from "../../../components/teams/TeamSelector";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    transform: "scale(1.06)",
    boxShadow: "0 2px 12px rgba(255,144,21,0.18)",
    color: "rgb(255,144,21)",
  },
  "&:active": {
    transform: "scale(0.96)",
    boxShadow: "0 1px 4px rgba(255,144,21,0.12)",
  },
};

const yearChevronSx = {
  minWidth: 36,
  minHeight: 36,
  borderRadius: "10px",
  color: "rgba(0,0,0,0.6)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
  },
  "&:active": {
    transform: "scale(0.93)",
  },
};

/**
 * @param {Object} props
 * @param {number}   props.selectedView  - 0 = Evaluations Collab, 1 = Evaluation DM, 2 = Evaluation Référent
 * @param {Function} props.onViewChange  - callback(viewIndex)
 * @param {Date}     props.date          - current Date object (year picker)
 * @param {Function} props.onDateChange  - callback(newDate)
 * @param {Function} props.onExport      - export excel callback
 * @param {boolean}  props.showExport    - whether to show the export button
 * @param {boolean}  props.isDMView      - if true, hide the view toggle (DM standalone)
 */
export default function MobileEvaluationsTopMenu({
  selectedView,
  onViewChange,
  date,
  onDateChange,
  onExport,
  showExport,
  isDMView,
}) {
  const handleViewChange = (_event, newValue) => {
    if (newValue !== null) {
      onViewChange(newValue);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 1px 12px rgba(0,40,128,0.05), 0 0.5px 3px rgba(0,0,0,0.03)",
        px: 1.5,
        pt: 0.75,
        pb: 1,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Title + Export button row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            pl: 0.5,
            letterSpacing: "-0.025em",
            color: "rgb(0,0,0)",
            transition: "color 0.25s ease, opacity 0.25s ease",
            lineHeight: 1.3,
          }}
        >
          Évaluations
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {showExport && (
            <Tooltip title="Export" arrow>
              <IconButton
                onClick={onExport}
                size="large"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: "1.35rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* TeamSelector row */}
      <Box sx={{ px: 0.5 }}>
        <TeamSelector includeMyTeam />
      </Box>

      {/* View toggle row (only if not DM standalone view) */}
      {!isDMView && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            px: 0.5,
            pb: 0.5,
          }}
        >
          <ToggleButtonGroup
            value={selectedView}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{
              width: "100%",
              "& .MuiToggleButton-root": {
                flex: 1,
                minHeight: 36,
                fontSize: "0.82rem",
                fontWeight: 600,
                textTransform: "none",
                letterSpacing: "-0.01em",
                border: "1px solid rgba(255,144,21,0.25)",
                color: "rgba(255,144,21,0.65)",
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": {
                  backgroundColor: "rgba(255,144,21,0.06)",
                  color: "rgba(255,144,21,0.85)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(255,144,21,0.12)",
                  color: "rgb(255,144,21)",
                  fontWeight: 700,
                  borderColor: "rgba(255,144,21,0.4)",
                  "&:hover": {
                    backgroundColor: "rgba(255,144,21,0.16)",
                  },
                },
              },
            }}
          >
            <ToggleButton value={0}>Collab</ToggleButton>
            <ToggleButton value={1}>DM</ToggleButton>
            <ToggleButton value={2}>Référent</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Year picker row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          pt: 0.25,
        }}
      >
        <IconButton
          size="small"
          onClick={() =>
            onDateChange(new Date(date.getFullYear() - 1, 0, 1))
          }
          sx={yearChevronSx}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            minWidth: 50,
            textAlign: "center",
            fontSize: "0.95rem",
            color: "rgba(0,0,0,0.8)",
            userSelect: "none",
          }}
        >
          {date.getFullYear()}
        </Typography>
        <IconButton
          size="small"
          onClick={() =>
            onDateChange(new Date(date.getFullYear() + 1, 0, 1))
          }
          sx={yearChevronSx}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
